import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BackupRestore } from "@/pages/admin/BackupRestore";
import { LiveSync } from "@/pages/admin/LiveSync";

export function AdminSettings() {
  const { refreshSettings } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch((err) => toast.error(errorMessage(err))); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", patch);
      setS(data);
      refreshSettings();
      toast.success("Settings saved");
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  if (!s) return null;
  const freeMode = s.site_mode === "free";
  return (
    <div data-testid="admin-settings" className="grid lg:grid-cols-3 gap-6">
      <div className={`rounded-2xl p-7 border ${freeMode ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30" : "bg-card"}`}>
        <p className="data-label">Site mode</p>
        <div className="mt-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-lg">{freeMode ? "Free Mode" : "Subscription Mode"}</p>
            <p className="text-sm text-muted-foreground mt-1">{freeMode ? "Maps open to everyone. Active subscribers' billing is paused (no refund, no lockout)." : "Maps gated behind premium. Billing runs normally."}</p>
          </div>
          <Switch data-testid="admin-mode-toggle-switch" checked={freeMode} disabled={saving} onCheckedChange={(v) => save({ site_mode: v ? "free" : "subscription" })} className="data-[state=checked]:bg-emerald-600" />
        </div>
        <p className="mt-4 text-xs text-muted-foreground">Switching back to Subscription Mode resumes paused plans and extends their period by the paused duration.</p>
      </div>

      <div className="rounded-2xl border bg-card p-7">
        <p className="data-label">Subscription price</p>
        <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); save({ price_inr: Number(s.price_inr) }); }}>
          <Label htmlFor="price">Monthly price (INR)</Label>
          <div className="flex gap-2">
            <div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-muted-foreground">₹</span><Input id="price" data-testid="admin-price-input" type="number" min={1} value={s.price_inr} onChange={(e) => setS({ ...s, price_inr: e.target.value })} className="pl-7 h-11 rounded-xl font-mono" /></div>
            <Button data-testid="admin-save-settings-button" type="submit" disabled={saving} className="h-11 rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950">Save</Button>
          </div>
          <p className="text-xs text-muted-foreground">Applies to new checkouts immediately. Default ₹499.</p>
        </form>
      </div>

      <div className="rounded-2xl border bg-card p-7">
        <p className="data-label">Default map provider</p>
        <div className="mt-4 space-y-3">
          <Label>Site-wide default engine</Label>
          <Select value={s.default_map_provider} onValueChange={(v) => save({ default_map_provider: v })}>
            <SelectTrigger data-testid="admin-map-provider-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="leaflet" data-testid="admin-map-provider-leaflet">Leaflet (OpenStreetMap / Esri)</SelectItem>
              <SelectItem value="google" data-testid="admin-map-provider-google">Google Maps</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Google Maps requires GOOGLE_MAPS_API_KEY in the backend .env; otherwise users see a notice and can switch to Leaflet.</p>
        </div>
      </div>
      <div className="lg:col-span-3"><LiveSync /></div>
      <div className="lg:col-span-3"><BackupRestore /></div>
    </div>
  );
}
